import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import useDebouncedValue from "@utils/useDebouncedValue";

import { AddressFormValues } from "./addressFormHelpers";
import { usePaginatedAddressQuery } from "./addressQueries";

const UNIQUE_NAME_ERROR_TYPE = "unique";

/** Async uniqueness check for the name field (create + edit flows). */
export const useValidateUniqueAddressName = ({
  shouldValidate,
  territoryId,
}: {
  shouldValidate: boolean;
  territoryId?: string | null;
}) => {
  const name = useWatch({ name: "name" });
  const trimmedName = typeof name === "string" ? name.trim() : "";
  // Present when editing; used to ignore the address's own row in search results.
  const editingAddressId = useWatch({ name: "id" });
  const debouncedName = useDebouncedValue(trimmedName, 500);
  const {
    setError,
    clearErrors,
    formState: { dirtyFields, errors },
  } = useFormContext<AddressFormValues>();

  // Avoid querying on load when the user hasn't changed the name yet.
  const isNameDirty = Boolean(dirtyFields.name);
  // Don't validate against stale query results while the user is still typing.
  const isDebounced = trimmedName === debouncedName;
  const shouldFetch =
    shouldValidate &&
    isNameDirty &&
    isDebounced &&
    // Short names are too noisy for a name filter and return too many rows.
    (debouncedName?.length ?? 0) > 3;

  const { data, isFetching } = usePaginatedAddressQuery(
    {
      filter: {
        name: debouncedName,
        ...(territoryId ? { territoryId } : {}),
      },
    },
    { enabled: shouldFetch }
  );

  useEffect(() => {
    const clearUniqueNameError = () => {
      // Only remove our error so required/pattern rules on name stay intact.
      if (errors.name?.type === UNIQUE_NAME_ERROR_TYPE) {
        clearErrors("name");
      }
    };

    if (!shouldValidate || !isNameDirty) {
      clearUniqueNameError();
      return;
    }

    if (!trimmedName || !shouldFetch || isFetching) {
      clearUniqueNameError();
      return;
    }

    const isDuplicate = data.some(
      (a) => a.name.trim() === trimmedName && a.id !== editingAddressId
    );

    if (isDuplicate) {
      setError("name", {
        type: UNIQUE_NAME_ERROR_TYPE,
        message: "An address with this name already exists",
      });
    } else {
      clearUniqueNameError();
    }
  }, [
    shouldValidate,
    isNameDirty,
    trimmedName,
    shouldFetch,
    isFetching,
    data,
    editingAddressId,
    errors.name,
    setError,
    clearErrors,
  ]);
};
